import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/gameConfig';
import { callGameEnd } from '../utils/gamebox';
import Ball from '../objects/Ball';
import Ring from '../objects/Ring';
import Obstacle from '../objects/Obstacle';

export default class PlayScene extends Phaser.Scene {
  private ball?: Ball;
  private currentRing?: Ring;
  private currentRingPosition?: { x: number; y: number }; // 現在のエネルギーの位置を保存
  private score: number = 0;
  private timeRemaining: number = GAME_CONFIG.timeLimit.initial;
  private gameStartTime: number = 0; // ゲーム開始時刻（秒）
  private isGameOver: boolean = false;
  private hasPlayedGameOverSound: boolean = false; // ゲームオーバー音声が既に再生されたか
  private isPaused: boolean = false;
  private isGameStarted: boolean = false; // ゲームが開始されたか
  
  // HUD
  private scoreText?: Phaser.GameObjects.Text;
  private timerBar?: Phaser.GameObjects.Graphics;
  private pauseButton?: Phaser.GameObjects.Image;
  private muteButton?: Phaser.GameObjects.Image;
  private isMuted: boolean = false;
  private debugButton?: Phaser.GameObjects.Rectangle;
  private debugButtonText?: Phaser.GameObjects.Text;
  
  // 評価テキスト（重複防止用）
  private currentEvaluationText?: Phaser.GameObjects.Text;
  
  // スタートボタン
  private startButton?: Phaser.GameObjects.Rectangle;
  private startButtonText?: Phaser.GameObjects.Text;
  
  // デバッグ用タイマー表示
  private debugTimerText?: Phaser.GameObjects.Text;
  
  // 段ボール画像（物理ボディ付き）
  private cardboard?: Phaser.Physics.Arcade.Sprite;
  
  // 地面タイルの配列（消滅管理用）
  private groundTiles: Phaser.GameObjects.Image[] = [];
  private groundTileDestroyOrder: number[] = []; // 消滅順序（中央から左右）
  
  // リングの出現位置（ランダム）
  
  private hasHitRing: boolean = false; // リングをヒット済みか（重複判定防止）
  private isOnGround: boolean = false; // 地面に接触しているか
  
  // 床
  private floor?: Phaser.Physics.Arcade.StaticGroup;
  
  private groundTileDestroyIndex: number = 0; // 現在の消滅インデックス
  private firstJumpTime: number | null = null; // 最初のジャンプの時刻（null = まだジャンプしていない）
  
  // リングとの衝突判定を保持（古いものを破棄するため）
  private ringOverlap?: Phaser.Physics.Arcade.Collider;
  
  // おじゃまキャラ
  private obstacles: Obstacle[] = [];
  private obstacleOverlaps: Phaser.Physics.Arcade.Collider[] = [];
  private causeObstacle?: Obstacle; // GameOverの原因となった犬
  private obstacleSpawnTimer?: Phaser.Time.TimerEvent; // おじゃまキャラ生成タイマー
  private timeSinceLastObstacle: number = 0; // 最後のおじゃまキャラ生成からの経過時間
  private nextObstaclePosition?: { x: number; y: number }; // 次のおじゃまキャラの出現位置
  private obstaclePreviewMarker?: Phaser.GameObjects.Image; // 出現位置のマーカー（画像）
  private updateTimerEvent?: Phaser.Time.TimerEvent; // タイマー更新イベント

  constructor() {
    super({ key: 'PlayScene' });
  }

  create(): void {
    // 既存のタイマーイベントをクリア（リトライ時のため）
    if (this.updateTimerEvent) {
      this.updateTimerEvent.remove();
      this.updateTimerEvent = undefined;
    }
    
    // ゲーム状態をリセット
    this.score = 0;
    this.timeRemaining = GAME_CONFIG.timeLimit.initial;
    this.gameStartTime = 0; // ゲーム開始時刻をリセット
    this.isGameOver = false;
    this.isPaused = false;
    this.isGameStarted = false; // ゲーム開始フラグをリセット
    this.hasHitRing = false;
    this.hasPlayedGameOverSound = false; // ゲームオーバー音声フラグをリセット
    this.timeSinceLastObstacle = 0; // おじゃまキャラ生成タイマーをリセット
    this.groundTiles = [];
    this.groundTileDestroyOrder = [];
    this.groundTileDestroyIndex = 0;
    this.firstJumpTime = null; // 最初のジャンプ時刻をリセット
    
    // BGMを開始（TitleSceneから開始されている場合は継続、そうでない場合は開始）
    // ミュート状態でない場合のみBGMを開始
    if (!this.isMuted) {
      const bgm = this.sound.get('bgm');
      if (!bgm || !bgm.isPlaying) {
        const newBgm = this.sound.add('bgm', { loop: true, volume: 0.5 });
        newBgm.play();
      }
    }
    
    const { width, height } = this.scale;
    const safeArea = Math.min(width, height) * GAME_CONFIG.safeAreaRatio;
    
    // 背景色を設定
    this.cameras.main.setBackgroundColor(0x72aabf);
    
    // 床を作成（画像を使用）
    this.floor = this.physics.add.staticGroup();
    // 床を画面の下部に配置
    const floorY = height - 100; // 画面下部から100px上
    const floorHeight = 200; // 画像の高さそのまま
    
    // 地面画像を横に並べて表示（ground.png: 100×200px）
    const groundImageWidth = 100; // 1枚の画像の幅
    const groundImageHeight = 200; // 1枚の画像の高さ
    const numTiles = 9; // 9枚並べる
    
    // 画像が読み込まれているか確認（テクスチャが存在するか）
    console.log('テクスチャ確認:', this.textures.exists('ground'), '利用可能なテクスチャ:', Object.keys(this.textures.list));
    
    if (!this.textures.exists('ground')) {
      console.error('ground画像が読み込まれていません！テクスチャを再確認します...');
      // テクスチャが存在しない場合、少し待ってから再試行
      this.time.delayedCall(100, () => {
        if (this.textures.exists('ground')) {
          console.log('ground画像が読み込まれました（遅延読み込み）');
          this.createGroundTiles(width, height, floorY, groundImageWidth, groundImageHeight, numTiles);
        } else {
          console.error('ground画像が読み込まれていません。パスを確認してください。');
        }
      });
    } else {
      this.createGroundTiles(width, height, floorY, groundImageWidth, groundImageHeight, numTiles);
    }
    
    // ボールを初期位置に配置（地面に置く）
    const ballRadius = GAME_CONFIG.physics.ballRadius; // 電球の半径
    const ballStartY = floorY - floorHeight / 2 - ballRadius; // 床の上端から電球の半径分下に配置
    this.ball = new Ball(this, width / 2, ballStartY);
    
    // 段ボール画像をCatと同じ位置に配置（物理ボディ付き、Catより上）
    this.cardboard = this.physics.add.sprite(width / 2, ballStartY, 'cardboard');
    this.cardboard.setOrigin(0.5, 0.5);
    this.cardboard.setDepth(26); // Cat（depth 25）より上
    
    // ダンボールの物理ボディを設定
    const cardboardBody = this.cardboard.body as Phaser.Physics.Arcade.Body;
    cardboardBody.setCollideWorldBounds(false); // 画面端で跳ね返らない
    cardboardBody.setAllowGravity(true); // 重力を有効化
    cardboardBody.setBounce(0.3); // 少し跳ね返る
    cardboardBody.setVelocity(0, 0); // 初期速度は0
    
    // コライダーサイズを調整（中央の地面タイル100pxの幅に合わせる）
    // 画像サイズは140×84pxだが、コライダーを100pxにすることで中央のタイルの上にのみ乗る
    const cardboardColliderWidth = 100; // 地面タイルの幅と同じ
    const cardboardColliderHeight = 84; // 画像の高さそのまま
    cardboardBody.setSize(cardboardColliderWidth, cardboardColliderHeight);
    
    // ダンボールと床の衝突判定を設定（地面にのみ反応）
    this.physics.add.collider(this.cardboard, this.floor);
    
    // 最初のリングを生成
    this.spawnRing();
    
    // HUD作成
    this.createHUD(safeArea);
    
    // 入力設定
    this.input.on('pointerdown', this.onTap, this);
    
    // 物理衝突設定（床との衝突判定を有効化 - 跳ね返りはするがGame Overにはしない）
    this.physics.add.collider(this.ball, this.floor, () => {
      // 床との衝突処理（必要に応じて追加）
    }, undefined, this);
    
    // おじゃまキャラの配列を初期化
    this.obstacles = [];
    this.obstacleOverlaps = [];
    this.causeObstacle = undefined; // GameOverの原因となった犬をリセット
    this.nextObstaclePosition = undefined;
    
    // マーカー用のImageを作成（非表示で初期化）
    this.obstaclePreviewMarker = this.add.image(0, 0, 'caution');
    this.obstaclePreviewMarker.setOrigin(0.5, 0.5);
    this.obstaclePreviewMarker.setDepth(24); // おじゃまキャラより前、ボールより後ろ
    this.obstaclePreviewMarker.setVisible(false);
    
    // スタートボタンを作成
    this.createStartButton(width, height);
    
    // デバッグ用：当たり判定を可視化（非表示）
    // this.physics.world.createDebugGraphic();
    // this.physics.world.drawDebug = true;
  }

  /**
   * 地面タイルを作成（100×200pxの画像を9枚横に並べる）
   */
  private createGroundTiles(
    width: number,
    height: number,
    floorY: number,
    groundImageWidth: number,
    groundImageHeight: number,
    numTiles: number
  ): void {
    const totalWidth = groundImageWidth * numTiles; // 900px
    const startX = (width - totalWidth) / 2; // 画面中央に配置
    
    // 地面タイルの配列を初期化
    this.groundTiles = [];
    this.groundTileDestroyOrder = [];
    
    // 消滅順序を計算（中央から左右に広がる）
    // 9枚の場合: 中央=4 → 3,5 → 2,6 → 1,7 → 0,8
    const centerIndex = Math.floor(numTiles / 2); // 中央のインデックス（4）
    this.groundTileDestroyOrder.push(centerIndex); // 最初に中央を消滅
    
    // 中央から左右に広がる順序で追加
    for (let offset = 1; offset <= centerIndex; offset++) {
      const leftIndex = centerIndex - offset;
      const rightIndex = centerIndex + offset;
      if (leftIndex >= 0) this.groundTileDestroyOrder.push(leftIndex);
      if (rightIndex < numTiles) this.groundTileDestroyOrder.push(rightIndex);
    }
    
    console.log('地面タイルを作成開始。枚数:', numTiles, 'totalWidth:', totalWidth, 'startX:', startX, 'floorY:', floorY);
    console.log('消滅順序:', this.groundTileDestroyOrder);
    
    for (let i = 0; i < numTiles; i++) {
      // 各タイルの中心X位置を計算
      const tileX = startX + groundImageWidth * i + groundImageWidth / 2;
      
      // 画像を通常のImageとして作成（縮小なし）
      const groundTile = this.add.image(tileX, floorY, 'ground');
      groundTile.setOrigin(0.5, 0.5);
      groundTile.setDepth(30); // depthを高くして確実に表示（ボールより後ろ、HUDより前）
      groundTile.setVisible(true);
      groundTile.setAlpha(1.0);
      
      // 物理ボディを追加
      this.physics.add.existing(groundTile, true);
      const tileBody = groundTile.body as Phaser.Physics.Arcade.StaticBody;
      tileBody.setSize(groundImageWidth, groundImageHeight); // 100×200px
      
      // フロアグループに追加
      if (this.floor) {
        this.floor.add(groundTile);
      }
      
      // 配列に保存（消滅管理用）
      this.groundTiles.push(groundTile);
      
      if (groundTile) {
        console.log(`地面タイル ${i + 1}/${numTiles}: X=${tileX.toFixed(0)}, Y=${floorY.toFixed(0)}, 表示サイズ=${groundTile.displayWidth.toFixed(0)}x${groundTile.displayHeight.toFixed(0)}`);
      }
    }
  }
  
  /**
   * 地面タイルを崩落アニメーション付きで消滅させる
   */
  private destroyGroundTile(tileIndex: number): void {
    if (tileIndex < 0 || tileIndex >= this.groundTiles.length) return;
    
    const tile = this.groundTiles[tileIndex];
    if (!tile || !tile.active || !tile.visible) return; // 既に非表示の場合は処理しない
    
    // フロアグループから削除（衝突判定を無効化）
    if (this.floor) {
      this.floor.remove(tile);
    }
    
    // 物理ボディを取得
    const tileBody = tile.body as Phaser.Physics.Arcade.StaticBody;
    
    // 崩落アニメーション（下方向に移動しながらフェードアウト）
    // 画像と物理ボディの位置を一緒に更新
    const startY = tile.y;
    const endY = startY + 300; // 下方向に300px移動
    
    this.tweens.add({
      targets: tile,
      y: endY,
      alpha: 0,
      duration: 800, // 0.8秒でアニメーション
      ease: 'Power2', // 加速する動き
      onUpdate: () => {
        // 物理ボディの位置も一緒に更新（コライダーも落下）
        if (tileBody) {
          tileBody.updateFromGameObject();
        }
      },
      onComplete: () => {
        // 物理ボディを無効化
        if (tileBody) {
          tileBody.enable = false;
        }
        // アニメーション完了後に削除
        tile.setVisible(false);
        tile.destroy();
      },
    });
    
    console.log(`地面タイル ${tileIndex + 1} を消滅させました（コライダーも落下）`);
  }

  /**
   * ミッドセンチュリーモダン背景を作成
   */
  private createMidCenturyBackground(width: number, height: number): void {
    // グラデーション背景を作成
    const bgGraphics = this.add.graphics();
    
    // 上から下へのグラデーション（温かみのある色合い）
    const topColor = GAME_CONFIG.colors.backgroundTop || 0xfaf5ed;
    const bottomColor = GAME_CONFIG.colors.backgroundBottom || 0xe8dcc0;
    
    // グラデーションを描画（縦方向）
    for (let y = 0; y < height; y++) {
      const ratio = y / height;
      const r = Math.floor((topColor >> 16 & 0xFF) * (1 - ratio) + (bottomColor >> 16 & 0xFF) * ratio);
      const g = Math.floor((topColor >> 8 & 0xFF) * (1 - ratio) + (bottomColor >> 8 & 0xFF) * ratio);
      const b = Math.floor((topColor & 0xFF) * (1 - ratio) + (bottomColor & 0xFF) * ratio);
      const color = (r << 16) | (g << 8) | b;
      
      bgGraphics.fillStyle(color, 1);
      bgGraphics.fillRect(0, y, width, 1);
    }
    
    // ミッドセンチュリーモダンの装飾的なパターン（有機的な曲線や幾何学的な形状）
    // 淡い装飾的な円を描画
    const patternColor = GAME_CONFIG.colors.gridLine || 0xe8dcc0;
    bgGraphics.fillStyle(patternColor, 0.1);
    
    // 装飾的な円をランダムに配置
    for (let i = 0; i < 8; i++) {
      const x = Phaser.Math.Between(0, width);
      const y = Phaser.Math.Between(0, height);
      const radius = Phaser.Math.Between(30, 80);
      bgGraphics.fillCircle(x, y, radius);
    }
    
    bgGraphics.setDepth(0); // 最背面
    bgGraphics.setScrollFactor(0);
  }

  private createHUD(safeArea: number): void {
    const { width, height } = this.scale;
    
    const cardPadding = 20;
    const cardRadius = 20; // より大きな角丸（ミッドセンチュリーモダン）
    const cardSpacing = 16;
    
    // スコアカード（ミッドセンチュリーモダンスタイル）
    const scoreCardWidth = 120; // 200から120に短縮
    const scoreCardHeight = 80;
    const scoreCardX = safeArea;
    const scoreCardY = safeArea;
    
    // スコアカードの背景（白、角丸）
    const scoreCardBg = this.add.graphics();
    scoreCardBg.fillStyle(GAME_CONFIG.colors.uiBackground, 1);
    scoreCardBg.fillRoundedRect(scoreCardX, scoreCardY, scoreCardWidth, scoreCardHeight, cardRadius);
    scoreCardBg.lineStyle(1, GAME_CONFIG.colors.uiBorder, 1);
    scoreCardBg.strokeRoundedRect(scoreCardX, scoreCardY, scoreCardWidth, scoreCardHeight, cardRadius);
    scoreCardBg.setDepth(100);
    scoreCardBg.setScrollFactor(0);
    
    // スコアラベル
    const scoreLabel = this.add.text(scoreCardX + cardPadding, scoreCardY + cardPadding, 'SCORE', {
      fontSize: '14px',
      fontFamily: 'Tiny5',
      color: `#${GAME_CONFIG.colors.uiGray.toString(16).padStart(6, '0')}`,
    });
    scoreLabel.setDepth(101);
    scoreLabel.setScrollFactor(0);
    
    // スコア表示
    this.scoreText = this.add.text(scoreCardX + cardPadding, scoreCardY + cardPadding + 20, '0', {
      fontSize: '32px',
      fontFamily: 'Tiny5',
      color: `#${GAME_CONFIG.colors.uiForeground.toString(16).padStart(6, '0')}`,
      fontStyle: 'bold',
    });
    this.scoreText.setDepth(101);
    this.scoreText.setScrollFactor(0);
    
    // ボタンサイズ（一時停止ボタンとミュートボタンを同じサイズに統一）
    const buttonSize = 50;
    
    // タイマーカード（ボタンとの間にスペースを確保）
    const buttonsTotalWidth = buttonSize * 2 + cardSpacing; // 2つのボタン + スペース
    const buttonCardX = width - safeArea - buttonsTotalWidth;
    const buttonCardY = safeArea;
    
    const timerCardWidth = buttonCardX - (safeArea + scoreCardWidth + cardSpacing) - cardSpacing;
    const timerCardHeight = 80;
    const timerCardX = safeArea + scoreCardWidth + cardSpacing;
    const timerCardY = safeArea;
    
    // タイマーカードの背景
    const timerCardBg = this.add.graphics();
    timerCardBg.fillStyle(GAME_CONFIG.colors.uiBackground, 1);
    timerCardBg.fillRoundedRect(timerCardX, timerCardY, timerCardWidth, timerCardHeight, cardRadius);
    timerCardBg.lineStyle(1, GAME_CONFIG.colors.uiBorder, 1);
    timerCardBg.strokeRoundedRect(timerCardX, timerCardY, timerCardWidth, timerCardHeight, cardRadius);
    timerCardBg.setDepth(100);
    timerCardBg.setScrollFactor(0);
    
    // タイマーバー
    const barWidth = timerCardWidth - cardPadding * 2;
    const barHeight = 8;
    const barX = timerCardX + cardPadding;
    const barY = timerCardY + cardPadding + 20;
    
    this.timerBar = this.add.graphics();
    this.timerBar.setDepth(101);
    this.timerBar.setScrollFactor(0);
    this.updateTimerBar();
    
    // 一時停止ボタン（左側）
    const pauseButtonX = buttonCardX;
    const pauseButtonBg = this.add.graphics();
    pauseButtonBg.fillStyle(GAME_CONFIG.colors.uiBackground, 1);
    pauseButtonBg.fillRoundedRect(pauseButtonX, buttonCardY, buttonSize, buttonSize, cardRadius);
    pauseButtonBg.lineStyle(1, GAME_CONFIG.colors.uiBorder, 1);
    pauseButtonBg.strokeRoundedRect(pauseButtonX, buttonCardY, buttonSize, buttonSize, cardRadius);
    pauseButtonBg.setDepth(100);
    pauseButtonBg.setScrollFactor(0);
    
    // ポーズボタンの画像
    const pauseButtonCenterX = pauseButtonX + buttonSize / 2;
    const pauseButtonCenterY = buttonCardY + buttonSize / 2;
    this.pauseButton = this.add.image(pauseButtonCenterX, pauseButtonCenterY, 'stop');
    this.pauseButton.setDisplaySize(buttonSize - 10, buttonSize - 10);
    this.pauseButton.setOrigin(0.5, 0.5);
    this.pauseButton.setDepth(101);
    this.pauseButton.setScrollFactor(0);
    this.pauseButton.setInteractive({ useHandCursor: true });
    this.pauseButton.on('pointerdown', () => {
      // ボタン効果音を再生（ミュート状態でない場合のみ）
      if (!this.isMuted) {
        const buttonSound = this.sound.add('button', { volume: 0.5 });
        buttonSound.play();
      }
      this.pauseGame();
    });
    
    // ミュートボタン（一時停止ボタンの右側に横並び）
    const muteButtonX = pauseButtonX + buttonSize + cardSpacing;
    
    // ミュートボタンの背景（角丸）
    const muteButtonBg = this.add.graphics();
    muteButtonBg.fillStyle(GAME_CONFIG.colors.uiBackground, 1);
    muteButtonBg.fillRoundedRect(muteButtonX, buttonCardY, buttonSize, buttonSize, cardRadius);
    muteButtonBg.lineStyle(1, GAME_CONFIG.colors.uiBorder, 1);
    muteButtonBg.strokeRoundedRect(muteButtonX, buttonCardY, buttonSize, buttonSize, cardRadius);
    muteButtonBg.setDepth(100);
    muteButtonBg.setScrollFactor(0);
    
    // ミュートボタンの画像
    this.muteButton = this.add.image(muteButtonX + buttonSize / 2, buttonCardY + buttonSize / 2, this.isMuted ? 'sounds_off' : 'sounds_on');
    this.muteButton.setDisplaySize(buttonSize - 10, buttonSize - 10);
    this.muteButton.setOrigin(0.5, 0.5);
    this.muteButton.setDepth(101);
    this.muteButton.setScrollFactor(0);
    this.muteButton.setInteractive({ useHandCursor: true });
    this.muteButton.on('pointerdown', () => {
      // ボタン効果音を再生（ミュート状態でない場合のみ）
      if (!this.isMuted) {
        const buttonSound = this.sound.add('button', { volume: 0.5 });
        buttonSound.play();
      }
      this.toggleMute();
    });
    
    // デバッグボタン（SCORE n OVER表示用）
    const debugButtonX = muteButtonX;
    const debugButtonY = buttonCardY + buttonSize + cardSpacing;
    const debugButtonWidth = buttonSize * 2 + cardSpacing;
    const debugButtonHeight = 40;
    
    // デバッグボタンの背景
    const debugButtonBg = this.add.graphics();
    debugButtonBg.fillStyle(0x999999, 1);
    debugButtonBg.fillRoundedRect(debugButtonX, debugButtonY, debugButtonWidth, debugButtonHeight, cardRadius);
    debugButtonBg.lineStyle(1, GAME_CONFIG.colors.uiBorder, 1);
    debugButtonBg.strokeRoundedRect(debugButtonX, debugButtonY, debugButtonWidth, debugButtonHeight, cardRadius);
    debugButtonBg.setDepth(100);
    debugButtonBg.setScrollFactor(0);
    debugButtonBg.setVisible(false); // 非アクティブ（非表示）
    
    // デバッグボタン
    this.debugButton = this.add.rectangle(debugButtonX + debugButtonWidth / 2, debugButtonY + debugButtonHeight / 2, debugButtonWidth, debugButtonHeight, 0x999999, 1);
    this.debugButton.setDepth(101);
    this.debugButton.setScrollFactor(0);
    this.debugButton.setVisible(false); // 非アクティブ（非表示）
    this.debugButton.setInteractive({ useHandCursor: true });
    this.debugButton.on('pointerdown', () => {
      // クリック時に色を変更
      if (this.debugButton) {
        this.debugButton.setFillStyle(0x777777, 1);
      }
    });
    this.debugButton.on('pointerup', () => {
      // 離した時に色を元に戻し、SCORE n OVERを表示
      if (this.debugButton) {
        this.debugButton.setFillStyle(0x999999, 1);
      }
      // 現在のスコアで「SCORE n OVER」を表示
      this.createScoreAchievementText(this.score || 10);
    });
    this.debugButton.on('pointerout', () => {
      // ボタン外に出た時に色を元に戻す
      if (this.debugButton) {
        this.debugButton.setFillStyle(0x999999, 1);
      }
    });
    this.debugButton.on('pointercancel', () => {
      // タッチがキャンセルされた時（スワイプなど）に色を元に戻す
      if (this.debugButton) {
        this.debugButton.setFillStyle(0x999999, 1);
      }
    });
    
    // デバッグボタンのテキスト
    this.debugButtonText = this.add.text(debugButtonX + debugButtonWidth / 2, debugButtonY + debugButtonHeight / 2, 'DEBUG', {
      fontSize: '16px',
      fontFamily: 'Tiny5',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.debugButtonText.setOrigin(0.5);
    this.debugButtonText.setDepth(102);
    this.debugButtonText.setScrollFactor(0);
    this.debugButtonText.setVisible(false); // 非アクティブ（非表示）
    
    // デバッグ用タイマー表示（非表示）
    // this.debugTimerText = this.add.text(safeArea, height - safeArea - 50, '', {
    //   fontSize: '18px',
    //   fontFamily: 'monospace',
    //   color: '#ff0000',
    //   backgroundColor: '#000000',
    //   padding: { x: 10, y: 10 },
    // });
    // this.debugTimerText.setDepth(200);
    // this.debugTimerText.setScrollFactor(0);
    // this.debugTimerText.setOrigin(0, 1); // 左下基準
  }

  /**
   * スタートボタンを作成
   */
  private createStartButton(width: number, height: number): void {
    const buttonWidth = 300;
    const buttonHeight = 80;
    const buttonX = width / 2;
    const buttonY = height / 2;
    
    // ボタンの背景（黄色で視認性を向上）
    this.startButton = this.add.rectangle(buttonX, buttonY, buttonWidth, buttonHeight, 0xFFD66B, 1);
    this.startButton.setDepth(200);
    this.startButton.setScrollFactor(0);
    this.startButton.setInteractive({ useHandCursor: true });
    this.startButton.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // クリック時に色を変更
      if (this.startButton) {
        this.startButton.setFillStyle(0xD9B65B, 1);
      }
      // イベントの伝播を止めて、電球がジャンプしないようにする
      if (pointer.event) {
        pointer.event.stopPropagation();
        pointer.event.preventDefault();
      }
    });
    this.startButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      // 離した時に色を元に戻し、ボタンの動作を実行
      if (this.startButton) {
        this.startButton.setFillStyle(0xFFD66B, 1);
      }
      // ボタン効果音を再生
      const buttonSound = this.sound.add('button', { volume: 0.5 });
      buttonSound.play();
      
      // シーンのpointerdownイベントを一時的に無効化（電球がジャンプしないようにするため）
      this.input.off('pointerdown', this.onTap, this);
      
      // ゲーム開始フラグを先に設定（念のため）
      this.isGameStarted = true;
      
      // ゲームを開始
      this.startGame();
      
      // 次のフレームでシーンのpointerdownイベントを再有効化
      this.time.delayedCall(100, () => {
        if (!this.isGameOver) {
          this.input.on('pointerdown', this.onTap, this);
        }
      });
    });
    this.startButton.on('pointerout', () => {
      // ボタン外に出た時に色を元に戻す
      if (this.startButton) {
        this.startButton.setFillStyle(0xFFD66B, 1);
      }
    });
    this.startButton.on('pointercancel', () => {
      // タッチがキャンセルされた時（スワイプなど）に色を元に戻す
      if (this.startButton) {
        this.startButton.setFillStyle(0xFFD66B, 1);
      }
    });
    
    // ボタンテキスト
    this.startButtonText = this.add.text(buttonX, buttonY, 'GO', {
      fontSize: '48px',
      fontFamily: 'Tiny5',
      color: '#000000',
      fontStyle: 'bold',
    });
    this.startButtonText.setOrigin(0.5);
    this.startButtonText.setDepth(201);
    this.startButtonText.setScrollFactor(0);
    // テキストはインタラクティブにしない（ボタンが処理する）
  }

  /**
   * ゲームを開始
   */
  private startGame(): void {
    // 既にゲームが開始されている場合は、スタートボタンが存在する場合のみ続行
    if (this.isGameStarted && !this.startButton) return;
    
    // まだ開始されていない場合は開始フラグを設定
    if (!this.isGameStarted) {
      this.isGameStarted = true;
    }
    
    // スタートボタンを非表示
    if (this.startButton) {
      this.startButton.destroy();
      this.startButton = undefined;
    }
    if (this.startButtonText) {
      this.startButtonText.destroy();
      this.startButtonText = undefined;
    }
    
    // ゲーム開始時刻を記録
    this.gameStartTime = this.time.now / 1000; // ミリ秒を秒に変換
    
    // タイマー開始
    this.updateTimerEvent = this.time.addEvent({
      delay: 16, // 約60FPS
      callback: this.updateTimer,
      callbackScope: this,
      loop: true,
    });
  }

  private spawnRing(): void {
    const { width, height } = this.scale;
    
    // おじゃまキャラと重ならないように位置を決定
    let x: number, y: number;
    let attempts = 0;
    const maxAttempts = 200; // 最大試行回数を大幅に増やす
    
    const ringRadius = GAME_CONFIG.ring.radius;
    const obstacleRadius = GAME_CONFIG.obstacle.radius;
    const minDistanceFromObstacle = ringRadius + obstacleRadius + 150; // 余裕を150pxに大幅に増やす（徹底的に被らないように）
    
    do {
      // X位置もランダム（マージンを考慮）
      x = Phaser.Math.Between(
        GAME_CONFIG.ring.sideMargin,
        width - GAME_CONFIG.ring.sideMargin
      );
      
      // Y位置もランダム（広い範囲）
      y = Phaser.Math.Between(GAME_CONFIG.ring.minY, GAME_CONFIG.ring.maxY);
      
      attempts++;
      
      let isValidPosition = true;
      
      // 既存のおじゃまキャラとの距離をチェック（半径を考慮して完全に重ならないように）
      for (const obstacle of this.obstacles) {
        const dx = x - obstacle.x;
        const dy = y - obstacle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < minDistanceFromObstacle) {
          isValidPosition = false;
          console.log(`[リング重複防止] 既存のおじゃまキャラとの距離が近すぎます: ${distance.toFixed(2)} < ${minDistanceFromObstacle} (おじゃまキャラ位置: ${obstacle.x.toFixed(0)}, ${obstacle.y.toFixed(0)}, 候補位置: ${x.toFixed(0)}, ${y.toFixed(0)})`);
          break;
        }
      }
      
      // 次に出現予定のおじゃまキャラの位置もチェック（重要！）
      if (isValidPosition && this.nextObstaclePosition) {
        const dx = x - this.nextObstaclePosition.x;
        const dy = y - this.nextObstaclePosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < minDistanceFromObstacle) {
          isValidPosition = false;
          console.log(`[リング重複防止] 次に出現予定のおじゃまキャラとの距離が近すぎます: ${distance.toFixed(2)} < ${minDistanceFromObstacle} (予定位置: ${this.nextObstaclePosition.x.toFixed(0)}, ${this.nextObstaclePosition.y.toFixed(0)}, 候補位置: ${x.toFixed(0)}, ${y.toFixed(0)})`);
        }
      }
      
      // 有効な位置が見つかった場合はループを抜ける
      if (isValidPosition) {
        break;
      }
    } while (attempts < maxAttempts);
    
    // 最大試行回数に達した場合は、おじゃまキャラから最も離れた位置を選択
    if (attempts >= maxAttempts) {
      let bestX = width / 2;
      let bestY = height / 2;
      let bestMinDistance = 0;
      
      // 複数の候補位置を試して、最も離れた位置を選択
      for (let i = 0; i < 50; i++) {
        const candidateX = Phaser.Math.Between(
          GAME_CONFIG.ring.sideMargin,
          width - GAME_CONFIG.ring.sideMargin
        );
        const candidateY = Phaser.Math.Between(
          GAME_CONFIG.ring.minY,
          GAME_CONFIG.ring.maxY
        );
        
        // すべてのおじゃまキャラからの最小距離を計算
        let minDistance = Infinity;
        for (const obstacle of this.obstacles) {
          const dx = candidateX - obstacle.x;
          const dy = candidateY - obstacle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          minDistance = Math.min(minDistance, distance);
        }
        
        // 次に出現予定のおじゃまキャラもチェック
        if (this.nextObstaclePosition) {
          const dx = candidateX - this.nextObstaclePosition.x;
          const dy = candidateY - this.nextObstaclePosition.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          minDistance = Math.min(minDistance, distance);
        }
        
        // より離れた位置を選択
        if (minDistance > bestMinDistance) {
          bestMinDistance = minDistance;
          bestX = candidateX;
          bestY = candidateY;
        }
      }
      
      console.log(`[リング重複防止] 最大試行回数に達しました。最も離れた位置を選択: (${bestX.toFixed(0)}, ${bestY.toFixed(0)}), 最小距離: ${bestMinDistance.toFixed(2)}`);
      x = bestX;
      y = bestY;
    }
    
    // 古いリングを削除
    if (this.currentRing) {
      this.currentRing.destroy();
    }
    
    // 新しいリングを作成
    this.currentRing = new Ring(this, x, y);
    
    // 現在のエネルギーの位置を保存（おじゃまキャラの生成時に使用）
    this.currentRingPosition = { x, y };
    
    // ボールとリングの衝突判定を設定
    // 古いoverlapを破棄してから新しいものを作成
    if (this.ringOverlap) {
      this.ringOverlap.destroy();
      this.ringOverlap = undefined;
    }
    
    // 衝突判定を確実に動作させるため、コールバック関数を定義
    const overlapCallback = (ball: any, ring: any) => {
      if (!this.hasHitRing && this.ball && this.currentRing) {
        this.onRingHit();
      }
    };
    // 衝突判定のコールバック関数を定義（第4引数でコールバック関数を指定）
    const processCallback = (ball: any, ring: any) => {
      // 両方の物理ボディが有効であることを確認
      if (ball.body && ring.body && ball.body.enable && ring.body.enable) {
        return true; // 衝突判定を実行
      }
      return false; // 衝突判定をスキップ
    };
    this.ringOverlap = this.physics.add.overlap(this.ball!, this.currentRing, overlapCallback, processCallback, this);
    
    // リングヒット状態をリセット
    this.hasHitRing = false;
    
    // おじゃまキャラの生成は、リング生成後に別のタイミングで行う（重複を防ぐため）
    // spawnObstacle()は呼ばない（updateTimer()で管理される）
  }
  
  /**
   * 次のおじゃまキャラの出現位置を計算（マーカー表示用）
   * フィッシュの位置と被らないように徹底的にチェック
   */
  private calculateNextObstaclePosition(): { x: number; y: number } | undefined {
    const { width, height } = this.scale;
    
    // フィッシュとおじゃまキャラが被らないように位置を決定
    let x: number, y: number;
    let attempts = 0;
    const maxAttempts = 200; // 最大試行回数を大幅に増やす
    
    const ringRadius = GAME_CONFIG.ring.radius;
    const obstacleRadius = GAME_CONFIG.obstacle.radius;
    const minDistanceFromRing = ringRadius + obstacleRadius + 150; // 余裕を150pxに大幅に増やす（徹底的に被らないように）
    
    do {
      // X位置もランダム（マージンを考慮）
      x = Phaser.Math.Between(
        GAME_CONFIG.obstacle.sideMargin,
        width - GAME_CONFIG.obstacle.sideMargin
      );
      
      // Y位置もランダム（広い範囲）
      y = Phaser.Math.Between(GAME_CONFIG.obstacle.minY, GAME_CONFIG.obstacle.maxY);
      
      attempts++;
      
      let isValidPosition = true;
      
      // フィッシュ（currentRing）が存在する場合は、必ずチェック
      if (this.currentRing) {
        const ringX = this.currentRing.x;
        const ringY = this.currentRing.y;
        
        const ringDx = x - ringX;
        const ringDy = y - ringY;
        const ringDistance = Math.sqrt(ringDx * ringDx + ringDy * ringDy);
        
        // フィッシュとの距離が最小距離より小さい場合は無効
        if (ringDistance < minDistanceFromRing) {
          isValidPosition = false;
          console.log(`[フィッシュ重複防止] フィッシュとの距離が近すぎます: ${ringDistance.toFixed(2)} < ${minDistanceFromRing} (フィッシュ位置: ${ringX.toFixed(0)}, ${ringY.toFixed(0)}, 候補位置: ${x.toFixed(0)}, ${y.toFixed(0)})`);
          continue; // 次の試行へ
        }
      }
      
      // currentRingPositionも必ずチェック（念のため）
      if (isValidPosition && this.currentRingPosition) {
        const ringDx = x - this.currentRingPosition.x;
        const ringDy = y - this.currentRingPosition.y;
        const ringDistance = Math.sqrt(ringDx * ringDx + ringDy * ringDy);
        
        if (ringDistance < minDistanceFromRing) {
          isValidPosition = false;
          console.log(`[フィッシュ重複防止] フィッシュ位置との距離が近すぎます: ${ringDistance.toFixed(2)} < ${minDistanceFromRing} (フィッシュ位置: ${this.currentRingPosition.x.toFixed(0)}, ${this.currentRingPosition.y.toFixed(0)}, 候補位置: ${x.toFixed(0)}, ${y.toFixed(0)})`);
          continue; // 次の試行へ
        }
      }
      
      // 既存のおじゃまキャラからの距離をチェック
      if (isValidPosition) {
        for (const existingObstacle of this.obstacles) {
          const obsDx = x - existingObstacle.x;
          const obsDy = y - existingObstacle.y;
          const obsDistance = Math.sqrt(obsDx * obsDx + obsDy * obsDy);
          // 既存のおじゃまキャラの半径 + 新しいおじゃまキャラの半径 + 最小間隔
          const minDistance = existingObstacle.radius + GAME_CONFIG.obstacle.radius + 20; // 20pxの余裕
          if (obsDistance < minDistance) {
            isValidPosition = false;
            break;
          }
        }
      }
      
      // 有効な位置が見つかった場合はループを抜ける
      if (isValidPosition) {
        console.log(`[フィッシュ重複防止] 有効な位置を見つけました: (${x.toFixed(0)}, ${y.toFixed(0)})`);
        break;
      }
    } while (attempts < maxAttempts);
    
    // 最大試行回数に達した場合は、フィッシュから最も離れた位置を強制的に設定
    if (attempts >= maxAttempts) {
      console.warn(`[フィッシュ重複防止] 最大試行回数に達しました。フィッシュから最も離れた位置を選択します。`);
      
      // フィッシュの位置から離れた位置を計算
      let bestX = width / 2;
      let bestY = height / 2;
      let bestMinDistance = 0;
      
      // 複数の候補位置を試して、最も離れた位置を選択（試行回数を大幅に増やす）
      for (let i = 0; i < 200; i++) {
        const candidateX = Phaser.Math.Between(
          GAME_CONFIG.obstacle.sideMargin,
          width - GAME_CONFIG.obstacle.sideMargin
        );
        const candidateY = Phaser.Math.Between(
          GAME_CONFIG.obstacle.minY,
          GAME_CONFIG.obstacle.maxY
        );
        
        let minDistance = Infinity;
        
        // フィッシュ（currentRing）からの距離（半径を考慮）
        if (this.currentRing) {
          const ringDx = candidateX - this.currentRing.x;
          const ringDy = candidateY - this.currentRing.y;
          const ringDistance = Math.sqrt(ringDx * ringDx + ringDy * ringDy);
          
          // フィッシュの半径 + おじゃまキャラの半径を引いた実際の距離
          const actualDistance = ringDistance - ringRadius - obstacleRadius;
          minDistance = Math.min(minDistance, actualDistance);
        }
        
        // currentRingPositionもチェック
        if (this.currentRingPosition) {
          const ringDx = candidateX - this.currentRingPosition.x;
          const ringDy = candidateY - this.currentRingPosition.y;
          const ringDistance = Math.sqrt(ringDx * ringDx + ringDy * ringDy);
          
          // フィッシュの半径 + おじゃまキャラの半径を引いた実際の距離
          const actualDistance = ringDistance - ringRadius - obstacleRadius;
          minDistance = Math.min(minDistance, actualDistance);
        }
        
        // 既存のおじゃまキャラからの距離
        for (const existingObstacle of this.obstacles) {
          const obsDx = candidateX - existingObstacle.x;
          const obsDy = candidateY - existingObstacle.y;
          const obsDistance = Math.sqrt(obsDx * obsDx + obsDy * obsDy);
          const minObsDistance = obsDistance - existingObstacle.radius - obstacleRadius;
          minDistance = Math.min(minDistance, minObsDistance);
        }
        
        // フィッシュから十分離れている位置を選択（最小距離以上）
        if (minDistance >= minDistanceFromRing - ringRadius - obstacleRadius && minDistance > bestMinDistance) {
          bestMinDistance = minDistance;
          bestX = candidateX;
          bestY = candidateY;
        }
      }
      
      // 有効な位置が見つかった場合のみ使用
      if (bestMinDistance > 0) {
        console.log(`[フィッシュ重複防止] フィッシュから最も離れた位置を選択: (${bestX.toFixed(0)}, ${bestY.toFixed(0)}), 距離: ${bestMinDistance.toFixed(2)}`);
        x = bestX;
        y = bestY;
      } else {
        // それでも見つからない場合は、画面の端に配置（フィッシュから最も離れる）
        if (this.currentRing) {
          const ringX = this.currentRing.x;
          const ringY = this.currentRing.y;
          
          // フィッシュの位置から最も離れた画面端を選択
          const distToLeft = ringX;
          const distToRight = width - ringX;
          const distToTop = ringY;
          const distToBottom = height - ringY;
          
          const maxDist = Math.max(distToLeft, distToRight, distToTop, distToBottom);
          
          if (maxDist === distToLeft) {
            x = GAME_CONFIG.obstacle.sideMargin;
            y = ringY; // 同じY位置だが、X方向に離れる
          } else if (maxDist === distToRight) {
            x = width - GAME_CONFIG.obstacle.sideMargin;
            y = ringY;
          } else if (maxDist === distToTop) {
            x = ringX;
            y = GAME_CONFIG.obstacle.minY;
          } else {
            x = ringX;
            y = GAME_CONFIG.obstacle.maxY;
          }
          
          console.warn(`[フィッシュ重複防止] 画面端に配置: (${x.toFixed(0)}, ${y.toFixed(0)})`);
        }
      }
    }
    
    return { x, y };
  }
  
  private spawnObstacle(): void {
    // 事前に計算された位置を使用、なければ新しく計算
    let position: { x: number; y: number } | undefined;
    if (this.nextObstaclePosition) {
      position = this.nextObstaclePosition;
      console.log('告知位置を使用:', position.x, position.y);
    } else {
      position = this.calculateNextObstaclePosition();
      console.log('新規計算:', position?.x, position?.y);
    }
    
    if (!position) return;
    
    // 最終チェック: 現在のリングとの距離を確認（念のため）
    const ringRadius = GAME_CONFIG.ring.radius;
    const obstacleRadius = GAME_CONFIG.obstacle.radius;
    const minDistanceFromRing = ringRadius + obstacleRadius + 150;
    
    if (this.currentRing) {
      const dx = position.x - this.currentRing.x;
      const dy = position.y - this.currentRing.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < minDistanceFromRing) {
        console.warn(`[おじゃまキャラ重複防止] リングとの距離が近すぎます: ${distance.toFixed(2)} < ${minDistanceFromRing}。位置を再計算します。`);
        // 位置を再計算
        position = this.calculateNextObstaclePosition();
        if (!position) return;
      }
    }
    
    // currentRingPositionもチェック
    if (this.currentRingPosition) {
      const dx = position.x - this.currentRingPosition.x;
      const dy = position.y - this.currentRingPosition.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < minDistanceFromRing) {
        console.warn(`[おじゃまキャラ重複防止] リング位置との距離が近すぎます: ${distance.toFixed(2)} < ${minDistanceFromRing}。位置を再計算します。`);
        // 位置を再計算
        position = this.calculateNextObstaclePosition();
        if (!position) return;
      }
    }
    
    // マーカーを非表示
    if (this.obstaclePreviewMarker) {
      this.obstaclePreviewMarker.setVisible(false);
    }
    
    // おじゃまキャラを作成
    const obstacle = new Obstacle(this, position.x, position.y);
    this.obstacles.push(obstacle);
    
    // 犬の鳴き声を再生（ミュート状態でない場合のみ）
    if (!this.isMuted) {
      const dogBarkSound = this.sound.add('dog_bark', { volume: 0.5 });
      dogBarkSound.play();
    }
    
    // ボールとおじゃまキャラの衝突判定を設定
    const overlapCallback = (ball: any, obs: any) => {
      if (!this.isGameOver && this.ball) {
        // おじゃまキャラに触れたら猫を停止させてブルブル震える
        this.onObstacleHit(obstacle);
      }
    };
    
    const processCallback = (ball: any, obs: any) => {
      // 両方の物理ボディが有効であることを確認
      if (ball.body && obs.body && ball.body.enable && obs.body.enable) {
        return true; // 衝突判定を実行
      }
      return false; // 衝突判定をスキップ
    };
    
    const overlap = this.physics.add.overlap(this.ball!, obstacle, overlapCallback, processCallback, this);
    this.obstacleOverlaps.push(overlap);
  }
  
  /**
   * おじゃまキャラの出現位置マーカーを更新
   */
  private updateObstaclePreview(progress: number): void {
    if (!this.obstaclePreviewMarker || !this.nextObstaclePosition) return;
    
    // progress: 1.0（出現2秒前）から0.0（出現直前）まで
    const alpha = Math.min(1.0, progress * 1.5); // 出現に近づくほど明るく
    const scale = 0.8 + (1.0 - progress) * 0.4; // 出現に近づくほど大きくなる（0.8〜1.2倍）
    
    // マーカーの位置とサイズを設定
    this.obstaclePreviewMarker.setPosition(this.nextObstaclePosition.x, this.nextObstaclePosition.y);
    this.obstaclePreviewMarker.setScale(scale);
    this.obstaclePreviewMarker.setVisible(true);
    
    // 点滅効果（sin波で点滅）
    const blinkAlpha = alpha * (0.7 + Math.sin(this.time.now / 100) * 0.3);
    this.obstaclePreviewMarker.setAlpha(blinkAlpha);
  }
  
  private clearObstacles(): void {
    // 古いおじゃまキャラを削除（原因となった犬以外）
    const obstaclesToKeep: Obstacle[] = [];
    const overlapsToKeep: Phaser.Physics.Arcade.Collider[] = [];
    
    this.obstacles.forEach((obstacle, index) => {
      // 原因となった犬は残す
      if (obstacle === this.causeObstacle) {
        obstaclesToKeep.push(obstacle);
        if (this.obstacleOverlaps[index]) {
          overlapsToKeep.push(this.obstacleOverlaps[index]);
        }
      } else {
        // 原因となった犬以外は削除
        obstacle.destroy();
        if (this.obstacleOverlaps[index]) {
          this.obstacleOverlaps[index].destroy();
        }
      }
    });
    
    // 原因となった犬だけを残す
    this.obstacles = obstaclesToKeep;
    this.obstacleOverlaps = overlapsToKeep;
    
    // マーカーを非表示
    if (this.obstaclePreviewMarker) {
      this.obstaclePreviewMarker.setVisible(false);
    }
    this.nextObstaclePosition = undefined;
  }

  private onTap(): void {
    // スタートボタンが存在する場合は処理をスキップ（ボタンクリックと区別）
    if (this.startButton) {
      return;
    }
    
    // スタートボタンが存在しない場合でも、ゲームが開始されていない場合は処理をスキップ
    if (!this.isGameStarted || this.isGameOver || this.isPaused || !this.ball || !this.currentRing) {
      return;
    }
    
    const body = this.ball.body as Phaser.Physics.Arcade.Body;
    
    // ボールが静止状態の場合は重力を有効化してジャンプ
    if (this.ball.getIsStationary()) {
      body.setAllowGravity(true);
      this.ball.setIsStationary(false);
    }
    
    // 最初のジャンプを記録（地面の崩落開始タイミング）
    if (this.firstJumpTime === null) {
      this.firstJumpTime = this.time.now / 1000; // 秒単位で記録
      console.log('最初のジャンプを検知。地面の崩落を開始します。');
    }
    
    // リングの方向を計算
    const dx = this.currentRing.x - this.ball.x;
    const distance = Math.abs(dx);
    
    // 横向きの速度を設定（リングの方向へ）
    let velocityX = 0;
    if (distance > 0) {
      // リングの方向に一定の速度を設定（弱めに）
      const direction = dx > 0 ? 1 : -1;
      velocityX = direction * GAME_CONFIG.physics.ballJumpForce * 0.2; // 横方向はジャンプ力の20%（弱め）
    }
    
    // ジャンプ効果音を再生
    const jumpSound = this.sound.add('jump', { volume: 0.5 });
    jumpSound.play();
    
    // タップで上方向にジャンプし、横向きはリングの方向へ
    body.setVelocity(velocityX, -GAME_CONFIG.physics.ballJumpForce);
  }

  private updateTimer(): void {
    if (this.isGameOver || this.isPaused || !this.isGameStarted) return;
    
    this.timeRemaining -= 0.016; // 約60FPS想定
    
    // 地面タイルの消滅処理（最初のジャンプから時間経過に応じて中央から左右に消滅）
    // 最初のジャンプが行われていない場合は崩落しない
    if (this.groundTiles.length > 0 && this.groundTileDestroyOrder.length > 0 && this.firstJumpTime !== null) {
      const totalTiles = this.groundTiles.length;
      
      // 最初のジャンプからの経過時間を計算
      const elapsedSinceFirstJump = (this.time.now / 1000) - this.firstJumpTime;
      
      // 制限時間が0になったら、残っている全てのタイルを強制的に消滅させる
      if (this.timeRemaining <= 0) {
        // 残っている全てのタイルを消滅
        while (this.groundTileDestroyIndex < this.groundTileDestroyOrder.length) {
          const tileIndex = this.groundTileDestroyOrder[this.groundTileDestroyIndex];
          this.destroyGroundTile(tileIndex);
          this.groundTileDestroyIndex++;
        }
        this.timeRemaining = 0;
        this.onTimeUp();
        return;
      }
      
      // 最初のジャンプからの経過時間に基づいて崩落率を計算（崩落速度を遅くするため、経過時間を2倍にする）
      const timeRatio = Math.min(1.0, elapsedSinceFirstJump / (GAME_CONFIG.timeLimit.initial * 2)); // 0.0（最初のジャンプ）から1.0（制限時間の2倍経過）まで
      const targetDestroyCount = Math.floor(timeRatio * totalTiles); // 消滅すべきタイル数
      
      // まだ消滅していないタイルを消滅させる
      while (this.groundTileDestroyIndex < targetDestroyCount && this.groundTileDestroyIndex < this.groundTileDestroyOrder.length) {
        const tileIndex = this.groundTileDestroyOrder[this.groundTileDestroyIndex];
        this.destroyGroundTile(tileIndex);
        this.groundTileDestroyIndex++;
      }
    } else if (this.timeRemaining <= 0) {
      // 地面タイルがない場合のTimeUp処理
      this.timeRemaining = 0;
      this.onTimeUp();
      return;
    }
    
    // おじゃまキャラの生成タイマーを更新
    this.timeSinceLastObstacle += 0.016; // 約60FPS想定
    
    // ゲーム開始からの経過時間を計算
    const elapsedTime = (this.time.now / 1000) - this.gameStartTime;
    
    // 経過時間に応じて出現間隔を計算
    const timeReduction = elapsedTime * GAME_CONFIG.obstacle.timeIntervalReduction;
    const spawnInterval = Math.max(
      GAME_CONFIG.obstacle.minSpawnInterval,
      GAME_CONFIG.obstacle.baseSpawnInterval - timeReduction
    );
    
    // デバッグ情報を更新（非表示）
    // if (this.debugTimerText) {
    //   const debugInfo = [
    //     `gameStartTime: ${this.gameStartTime.toFixed(2)}`,
    //     `elapsedTime: ${elapsedTime.toFixed(2)}`,
    //     `timeSinceLastObstacle: ${this.timeSinceLastObstacle.toFixed(2)}`,
    //     `spawnInterval: ${spawnInterval.toFixed(2)}`,
    //     `timeRemaining: ${this.timeRemaining.toFixed(2)}`,
    //     `obstacles: ${this.obstacles.length}`,
    //   ].join('\n');
    //   this.debugTimerText.setText(debugInfo);
    // }
    
    // 出現間隔の残り時間を計算
    const timeUntilSpawn = spawnInterval - this.timeSinceLastObstacle;
    
    // 事前告知時間内になったら次の出現位置を計算してマーカーを表示
    if (timeUntilSpawn <= GAME_CONFIG.obstacle.previewDuration && timeUntilSpawn > 0) {
      // 次の出現位置を計算（まだ計算していない場合）
      if (!this.nextObstaclePosition) {
        this.nextObstaclePosition = this.calculateNextObstaclePosition();
        console.log('告知位置を計算:', this.nextObstaclePosition?.x, this.nextObstaclePosition?.y);
      }
      // マーカーを更新（progress: 1.0（出現2秒前）から0.0（出現直前）まで）
      if (this.nextObstaclePosition) {
        const progress = timeUntilSpawn / GAME_CONFIG.obstacle.previewDuration;
        this.updateObstaclePreview(progress);
      }
    } else {
      // 告知時間外の場合はマーカーを非表示（位置は保持）
      if (this.obstaclePreviewMarker) {
        this.obstaclePreviewMarker.setVisible(false);
      }
      // nextObstaclePositionはリセットしない（出現するまで保持）
    }
    
    // 出現間隔を超えたらおじゃまキャラを生成
    if (this.timeSinceLastObstacle >= spawnInterval) {
      this.spawnObstacle();
      this.timeSinceLastObstacle = 0;
      this.nextObstaclePosition = undefined; // 次の位置をリセット
    }
    
    this.updateTimerBar();
  }

  private updateTimerBar(): void {
    if (!this.timerBar) return;
    
    const { width } = this.scale;
    const safeArea = Math.min(width, width) * GAME_CONFIG.safeAreaRatio;
    const cardPadding = 16;
    const cardSpacing = 12;
    const scoreCardWidth = 120; // 200から120に短縮
    const buttonSize = 50; // 60から50に変更
    const buttonsTotalWidth = buttonSize * 2 + cardSpacing; // 2つのボタン + スペース
    
    // ボタンの位置を考慮してタイマーカードの幅を計算
    const buttonCardX = width - safeArea - buttonsTotalWidth;
    const timerCardWidth = buttonCardX - (safeArea + scoreCardWidth + cardSpacing) - cardSpacing;
    
    // タイマーカード内のバー位置
    const barWidth = timerCardWidth - cardPadding * 2;
    const barHeight = 8;
    const barX = safeArea + scoreCardWidth + cardSpacing + cardPadding;
    const barY = safeArea + cardPadding + 20;
    
    this.timerBar.clear();
    
    // 残り時間バー（ミッドセンチュリーモダンスタイル：角丸）
    const ratio = Math.max(0, Math.min(1, this.timeRemaining / GAME_CONFIG.timeLimit.initial));
    const fillWidth = barWidth * ratio;
    
    // ミッドセンチュリーモダンカラー：ミントグリーン→マスタードイエロー→ターコイズブルー
    const color = ratio > 0.5 ? GAME_CONFIG.colors.uiSecondary : ratio > 0.25 ? GAME_CONFIG.colors.uiAccent : GAME_CONFIG.colors.uiPrimary;
    
    if (fillWidth > 0) {
      this.timerBar.fillStyle(color, 1);
      this.timerBar.fillRoundedRect(barX, barY, fillWidth, barHeight, 4);
    }
  }

  update(): void {
    if (!this.isGameStarted || this.isGameOver || this.isPaused || !this.ball || !this.currentRing) return;
    
    // ボールの更新
    this.ball.update();
    
    // 猫（ボール）が画面下部に落下したらGameOver（横方向は問題なし）
    const { width, height } = this.scale;
    if (this.ball.y > height + 250) { // 画面下部250px以上下に落下したら
      console.log('猫が画面下部に落下しました。GameOver');
      // 落下のデスエリアに触れた直後に音声を再生（重複防止）
      if (!this.hasPlayedGameOverSound) {
        this.hasPlayedGameOverSound = true;
        
        // BGMを停止
        const bgm = this.sound.get('bgm');
        if (bgm) {
          bgm.stop();
        }
        // 念のため、全てのBGMキーのサウンドを停止
        const allSounds = this.sound.getAllPlaying();
        allSounds.forEach(sound => {
          if (sound.key === 'bgm') {
            sound.stop();
          }
        });
        
        // meow2.mp3を再生
        const meow2Sound = this.sound.add('meow2', { volume: 0.5 });
        meow2Sound.play();
        
        // meow2.mp3の再生が終わった後にgameover.mp3を再生
        meow2Sound.on('complete', () => {
          const gameoverSound = this.sound.add('gameover', { volume: 0.5 });
          gameoverSound.play();
        });
      }
      this.gameOver();
      return;
    }
    
    // 画面端で反対側から出現させる（ワープアラウンド）- 横方向のみ
    if (this.ball.x < -GAME_CONFIG.physics.ballRadius) {
      // 左端を超えたら右端から出現
      this.ball.setX(width + GAME_CONFIG.physics.ballRadius);
    } else if (this.ball.x > width + GAME_CONFIG.physics.ballRadius) {
      // 右端を超えたら左端から出現
      this.ball.setX(-GAME_CONFIG.physics.ballRadius);
    }
    
    // リングの更新（発光スプライトの追従）
    if (this.currentRing) {
      this.currentRing.update();
    }
    
    // おじゃまキャラの更新と削除（GameOverの原因となった犬は削除しない）
    const delta = this.game.loop.delta; // フレーム間の時間（ミリ秒）
    const obstaclesToRemove: Obstacle[] = [];
    
    this.obstacles.forEach((obstacle, index) => {
      obstacle.update(delta);
      
      // 生存時間を超えたおじゃまキャラを削除対象に追加（原因となった犬は除外）
      if (obstacle.isExpired() && obstacle !== this.causeObstacle) {
        obstaclesToRemove.push(obstacle);
      }
    });
    
    // 期限切れのおじゃまキャラを削除
    obstaclesToRemove.forEach(obstacle => {
      const index = this.obstacles.indexOf(obstacle);
      if (index !== -1) {
        // 対応する衝突判定も削除
        if (this.obstacleOverlaps[index]) {
          this.obstacleOverlaps[index].destroy();
          this.obstacleOverlaps.splice(index, 1);
        }
        obstacle.destroy();
        this.obstacles.splice(index, 1);
      }
    });
  }

  /**
   * TimeUpになった時の処理
   */
  private onTimeUp(): void {
    if (!this.ball || this.isGameOver) return;
    
    // まず猫を完全に停止させる（即座に停止）
    const body = this.ball.body as Phaser.Physics.Arcade.Body;
    const stopX = this.ball.x;
    const stopY = this.ball.y;
    
    // 物理ボディを完全に無効化（即座に停止）
    body.setVelocity(0, 0);
    body.setAllowGravity(false);
    body.setImmovable(true);
    body.setAcceleration(0, 0);
    body.setDrag(1000, 1000);
    body.enable = false; // 物理ボディを無効化して即座に停止
    
    // 位置を固定（物理エンジンの更新を防ぐ）
    this.ball.setX(stopX);
    this.ball.setY(stopY);
    
    // GameOverフラグを設定（update()での物理更新を防ぐ）
    this.isGameOver = true;
    
    // TimeUpの音声を再生（重複防止）
    if (!this.hasPlayedGameOverSound) {
      this.hasPlayedGameOverSound = true;
      
      // BGMを停止
      const bgm = this.sound.get('bgm');
      if (bgm) {
        bgm.stop();
      }
      // 念のため、全てのBGMキーのサウンドを停止
      const allSounds = this.sound.getAllPlaying();
      allSounds.forEach(sound => {
        if (sound.key === 'bgm') {
          sound.stop();
        }
      });
      
      // meow2.mp3を再生
      const meow2Sound = this.sound.add('meow2', { volume: 1.0 });
      meow2Sound.play();
      
      // meow2.mp3の再生が終わった後にgameover.mp3を再生
      meow2Sound.on('complete', () => {
        const gameoverSound = this.sound.add('gameover', { volume: 0.5 });
        gameoverSound.play();
      });
    }
    
    // 画面中央に"Time Up!"のテキストを表示
    const { width, height } = this.scale;
    const timeUpText = this.add.text(width / 2, height / 2, 'Time Up!', {
      fontSize: '80px',
      fontFamily: 'Tiny5',
      color: '#FF0000', // 赤色
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6,
    });
    timeUpText.setOrigin(0.5);
    timeUpText.setDepth(200);
    
    // ブルブルっと震えるアニメーション
    const shakeAmount = 5; // 震える幅（ピクセル）
    const shakeDuration = 500; // 震える時間（ミリ秒）
    const shakeCount = 10; // 震える回数
    
    const originalX = stopX;
    const originalY = stopY;
    
    // 震えるアニメーション（X方向とY方向に小さく振動）
    const shakeTween = this.tweens.add({
      targets: this.ball,
      x: {
        from: originalX - shakeAmount,
        to: originalX + shakeAmount,
      },
      y: {
        from: originalY - shakeAmount / 2,
        to: originalY + shakeAmount / 2,
      },
      duration: shakeDuration / shakeCount,
      repeat: shakeCount - 1,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        // 震え終わったら元の位置に戻す
        if (this.ball) {
          this.ball.setX(originalX);
          this.ball.setY(originalY);
        }
        // ゲームオーバー処理の続きを呼ぶ（isGameOverは既にtrueなので、おじゃまキャラのクリアとリザルト画面への遷移のみ）
        this.proceedToGameOver();
      },
    });
  }

  /**
   * ゲームオーバー処理の続き（おじゃまキャラのクリアとリザルト画面への遷移）
   */
  private proceedToGameOver(): void {
    // おじゃまキャラをクリア
    this.clearObstacles();
    
    // タイマーを停止
    if (this.updateTimerEvent) {
      this.updateTimerEvent.remove();
      this.updateTimerEvent = undefined;
    }
    
    // 少し遅延してからリザルト画面に遷移（ゲームオーバーの原因を確認できるように）
    this.time.delayedCall(1000, () => {
      // game_endを呼ぶ
      callGameEnd(this.score, () => {
        // コールバックが呼ばれたらリザルト画面へ
        this.scene.start('ResultScene', { score: this.score });
      });
    });
  }

  /**
   * おじゃまキャラに触れた時の処理
   */
  private onObstacleHit(obstacle: Obstacle): void {
    if (!this.ball || this.isGameOver) return;
    
    // GameOverの原因となった犬を記録
    this.causeObstacle = obstacle;
    
    // 犬に触れた直後に音声を再生（重複防止）
    if (!this.hasPlayedGameOverSound) {
      this.hasPlayedGameOverSound = true;
      
      // BGMを停止
      const bgm = this.sound.get('bgm');
      if (bgm) {
        bgm.stop();
      }
      // 念のため、全てのBGMキーのサウンドを停止
      const allSounds = this.sound.getAllPlaying();
      allSounds.forEach(sound => {
        if (sound.key === 'bgm') {
          sound.stop();
        }
      });
      
      // meow2.mp3を再生
      const meow2Sound = this.sound.add('meow2', { volume: 1.0 });
      meow2Sound.play();
      
      // meow2.mp3の再生が終わった後にgameover.mp3を再生
      meow2Sound.on('complete', () => {
        const gameoverSound = this.sound.add('gameover', { volume: 0.5 });
        gameoverSound.play();
      });
    }
    
    // 猫を完全に停止させる（即座に停止）
    const body = this.ball.body as Phaser.Physics.Arcade.Body;
    const stopX = this.ball.x;
    const stopY = this.ball.y;
    
    // 物理ボディを完全に無効化（即座に停止）
    body.setVelocity(0, 0);
    body.setAllowGravity(false);
    body.setImmovable(true);
    body.setAcceleration(0, 0);
    body.setDrag(1000, 1000);
    body.enable = false; // 物理ボディを無効化して即座に停止
    
    // 位置を固定（物理エンジンの更新を防ぐ）
    this.ball.setX(stopX);
    this.ball.setY(stopY);
    
    // ブルブルっと震えるアニメーション
    const shakeAmount = 5; // 震える幅（ピクセル）
    const shakeDuration = 500; // 震える時間（ミリ秒）
    const shakeCount = 10; // 震える回数
    
    const originalX = stopX;
    const originalY = stopY;
    
    // 震えるアニメーション（X方向とY方向に小さく振動）
    const shakeTween = this.tweens.add({
      targets: this.ball,
      x: {
        from: originalX - shakeAmount,
        to: originalX + shakeAmount,
      },
      y: {
        from: originalY - shakeAmount / 2,
        to: originalY + shakeAmount / 2,
      },
      duration: shakeDuration / shakeCount,
      repeat: shakeCount - 1,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        // 震え終わったら元の位置に戻す
        if (this.ball) {
          this.ball.setX(originalX);
          this.ball.setY(originalY);
        }
        // ゲームオーバー処理を呼ぶ
        this.gameOver();
      },
    });
  }

  private onRingHit(): void {
    if (!this.ball || !this.currentRing || this.hasHitRing) return;
    
    console.log('Ring hit!'); // デバッグ用
    this.hasHitRing = true;
    
    // 猫の鳴き声を再生
    const meowSound = this.sound.add('meow', { volume: 0.5 });
    meowSound.play();
    
    // スコア加算（基本スコア）
    const points = GAME_CONFIG.score.base;
    this.score += points;
    
    // ボールを発光
    this.ball.glow();
    
    // 10点ごとにスコア達成テキストを画面上部に表示
    if (this.score % 10 === 0 && this.score > 0) {
      this.createScoreAchievementText(this.score);
    }
    
    // 10点ごとに評価テキストを表示
    if (this.score % 10 === 0 && this.score > 0) {
      // Lottieアニメーションを再生（評価テキスト表示時のみ）
      this.playLottieAnimation(this.currentRing.x, this.currentRing.y);
      this.createScoreEvaluationText(this.currentRing.x, this.currentRing.y, this.score);
    } else {
      // 「Meow」表示（10点ごとでない場合のみ）
      this.createNyaaEffect(this.currentRing.x, this.currentRing.y);
    }
    
    // 制限時間をリセット
    this.timeRemaining = GAME_CONFIG.timeLimit.initial;
    
    // スコア表示更新
    if (this.scoreText) {
      this.scoreText.setText(`${this.score}`);
    }
    
    // 次のリングを生成（スピードアップのため遅延を短縮）
    this.time.delayedCall(150, () => {
      this.spawnRing();
      // ボールの位置は継続（リセットしない）
    });
  }

  /**
   * Lottieアニメーション（パーティクル2）を再生
   */
  private playLottieAnimation2(x: number, y: number): void {
    // lottie-webが読み込まれているか確認
    if (typeof (window as any).lottie === 'undefined') {
      console.warn('lottie-web is not loaded');
      return;
    }

    // Phaserの座標をDOM座標に変換（ゲームキャンバスの位置を考慮）
    const gameCanvas = this.game.canvas;
    const canvasRect = gameCanvas.getBoundingClientRect();
    const gameContainer = document.getElementById('game-container');
    if (!gameContainer) return;
    
    // キャンバス内の座標をDOM座標に変換
    const scaleX = canvasRect.width / this.scale.width;
    const scaleY = canvasRect.height / this.scale.height;
    const domX = canvasRect.left + (x * scaleX);
    const domY = canvasRect.top + (y * scaleY);

    // アニメーション用のDOM要素を作成（サイズを大きく）
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = `${domX}px`;
    container.style.top = `${domY}px`;
    container.style.width = '300px'; // 200pxから300pxに拡大
    container.style.height = '300px'; // 200pxから300pxに拡大
    container.style.transform = 'translate(-50%, -50%)';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '1000';
    
    // ゲームコンテナに追加
    gameContainer.appendChild(container);

    // Lottieアニメーションを読み込んで再生（パーティクル2）
    const animation = (window as any).lottie.loadAnimation({
      container: container,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      path: './assets/animations/particle2.json',
    });

    // アニメーション完了後にDOM要素を削除
    animation.addEventListener('complete', () => {
      setTimeout(() => {
        container.remove();
      }, 100);
    });
  }

  /**
   * Lottieアニメーションを再生
   */
  private playLottieAnimation(x: number, y: number): void {
    // lottie-webが読み込まれているか確認
    if (typeof (window as any).lottie === 'undefined') {
      console.warn('lottie-web is not loaded');
      return;
    }

    // Phaserの座標をDOM座標に変換（ゲームキャンバスの位置を考慮）
    const gameCanvas = this.game.canvas;
    const canvasRect = gameCanvas.getBoundingClientRect();
    const gameContainer = document.getElementById('game-container');
    if (!gameContainer) return;
    
    const containerRect = gameContainer.getBoundingClientRect();
    
    // キャンバス内の座標をDOM座標に変換
    const scaleX = canvasRect.width / this.scale.width;
    const scaleY = canvasRect.height / this.scale.height;
    const domX = canvasRect.left + (x * scaleX);
    const domY = canvasRect.top + (y * scaleY);

    // アニメーション用のDOM要素を作成（サイズを大きく）
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = `${domX}px`;
    container.style.top = `${domY}px`;
    container.style.width = '300px'; // 200pxから300pxに拡大
    container.style.height = '300px'; // 200pxから300pxに拡大
    container.style.transform = 'translate(-50%, -50%)';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '1000';
    
    // ゲームコンテナに追加
    gameContainer.appendChild(container);

    // Lottieアニメーションを読み込んで再生
    const animation = (window as any).lottie.loadAnimation({
      container: container,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      path: './assets/animations/particle1.json',
    });

    // アニメーション完了後にDOM要素を削除
    animation.addEventListener('complete', () => {
      setTimeout(() => {
        container.remove();
      }, 100);
    });
  }

  /**
   * スコア達成テキストを画面上部に表示（デバッグ用: 1点ごと）
   * 10種類の異なるアニメーションバージョンを用意
   */
  private createScoreAchievementText(score: number): void {
    const { width, height } = this.scale;
    
    // スコアオーバー効果音を再生
    const overScoreSound = this.sound.add('over_score', { volume: 0.5 });
    overScoreSound.play();
    
    // スロットの「オーバー」スタイルで表示（例: "SCORE 10 OVER", "SCORE 20 OVER"）
    const achievementText = this.add.text(width / 2, height * 0.15, `SCORE ${score} OVER`, {
      fontSize: '40px',
      fontFamily: 'Tiny5',
      color: '#FFD700', // 金色
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    achievementText.setOrigin(0.5);
    achievementText.setDepth(110);
    
    // バージョン10のみを使用
    this.playAchievementVersion10(achievementText);
    
    // パーティクル2アニメーションも再生
    this.playLottieAnimation2(this.scale.width / 2, this.scale.height * 0.15);
  }

  // バージョン1: 回転しながら拡大（現在のバージョン）
  private playAchievementVersion1(text: Phaser.GameObjects.Text): void {
    text.setScale(0.1);
    text.setAlpha(0);
    text.setRotation(-0.2);
    
    this.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 1.5,
      scaleY: 1.5,
      rotation: 0.1,
      duration: 500,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          scaleX: 1.3,
          scaleY: 1.3,
          rotation: 0,
          duration: 200,
          ease: 'Power1',
          onComplete: () => {
            this.tweens.add({
              targets: text,
              scaleX: 1.4,
              scaleY: 1.4,
              duration: 150,
              yoyo: true,
              repeat: 1,
              ease: 'Power1',
              onComplete: () => {
                this.tweens.add({
                  targets: text,
                  alpha: 0,
                  scaleX: 1.6,
                  scaleY: 1.6,
                  rotation: 0.1,
                  duration: 800,
                  delay: 600,
                  ease: 'Power2',
                  onComplete: () => text.destroy(),
                });
              },
            });
          },
        });
      },
    });
    
    const colors = [0xFFD700, 0xFFA500, 0xFFFF00, 0xFFD700];
    let colorIndex = 0;
    const colorTween = this.tweens.addCounter({
      from: 0,
      to: colors.length - 1,
      duration: 2000,
      onUpdate: (tween) => {
        const value = tween.getValue();
        if (value !== null) {
          const index = Math.floor(value);
          if (index !== colorIndex && index < colors.length) {
            colorIndex = index;
            text.setColor(`#${colors[index].toString(16).padStart(6, '0')}`);
          }
        }
      },
    });
    this.time.delayedCall(2000, () => colorTween.stop());
  }

  // バージョン2: 上下にバウンス
  private playAchievementVersion2(text: Phaser.GameObjects.Text): void {
    const startY = text.y;
    text.setScale(0.3);
    text.setAlpha(0);
    
    this.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 1.4,
      scaleY: 1.4,
      y: startY - 50,
      duration: 400,
      ease: 'Bounce.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          y: startY + 30,
          duration: 200,
          yoyo: true,
          repeat: 2,
          ease: 'Power1',
          onComplete: () => {
            this.tweens.add({
              targets: text,
              alpha: 0,
              scaleX: 1.6,
              scaleY: 1.6,
              y: startY - 100,
              duration: 700,
              delay: 500,
              ease: 'Power2',
              onComplete: () => text.destroy(),
            });
          },
        });
      },
    });
  }

  // バージョン3: 左右にスイング
  private playAchievementVersion3(text: Phaser.GameObjects.Text): void {
    const startX = text.x;
    text.setScale(0.2);
    text.setAlpha(0);
    text.setRotation(-0.5);
    
    this.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 1.3,
      scaleY: 1.3,
      x: startX + 100,
      rotation: 0.3,
      duration: 400,
      ease: 'Elastic.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          x: startX - 80,
          rotation: -0.2,
          duration: 300,
          yoyo: true,
          repeat: 1,
          ease: 'Power1',
          onComplete: () => {
            this.tweens.add({
              targets: text,
              alpha: 0,
              scaleX: 1.5,
              scaleY: 1.5,
              rotation: 0,
              duration: 600,
              delay: 400,
              ease: 'Power2',
              onComplete: () => text.destroy(),
            });
          },
        });
      },
    });
  }

  // バージョン4: 波打つような動き
  private playAchievementVersion4(text: Phaser.GameObjects.Text): void {
    text.setScale(0.5);
    text.setAlpha(0);
    
    this.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 1.5,
      scaleY: 0.8,
      duration: 300,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          scaleX: 0.9,
          scaleY: 1.4,
          duration: 200,
          yoyo: true,
          repeat: 3,
          ease: 'Sine.easeInOut',
          onComplete: () => {
            this.tweens.add({
              targets: text,
              alpha: 0,
              scaleX: 1.6,
              scaleY: 1.6,
              duration: 500,
              delay: 300,
              ease: 'Power2',
              onComplete: () => text.destroy(),
            });
          },
        });
      },
    });
  }

  // バージョン5: ズームイン・アウト
  private playAchievementVersion5(text: Phaser.GameObjects.Text): void {
    text.setScale(2.0);
    text.setAlpha(0);
    
    this.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 0.8,
      scaleY: 0.8,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          scaleX: 1.5,
          scaleY: 1.5,
          duration: 400,
          ease: 'Back.easeOut',
          onComplete: () => {
            this.tweens.add({
              targets: text,
              scaleX: 1.2,
              scaleY: 1.2,
              duration: 200,
              onComplete: () => {
                this.tweens.add({
                  targets: text,
                  alpha: 0,
                  scaleX: 0.5,
                  scaleY: 0.5,
                  duration: 600,
                  delay: 500,
                  ease: 'Power2',
                  onComplete: () => text.destroy(),
                });
              },
            });
          },
        });
      },
    });
  }

  // バージョン6: スピン回転
  private playAchievementVersion6(text: Phaser.GameObjects.Text): void {
    text.setScale(0.3);
    text.setAlpha(0);
    text.setRotation(0);
    
    this.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 1.4,
      scaleY: 1.4,
      rotation: Math.PI * 2,
      duration: 600,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          rotation: -Math.PI * 0.5,
          duration: 200,
          yoyo: true,
          repeat: 1,
          ease: 'Power1',
          onComplete: () => {
            this.tweens.add({
              targets: text,
              alpha: 0,
              scaleX: 1.7,
              scaleY: 1.7,
              rotation: Math.PI,
              duration: 500,
              delay: 400,
              ease: 'Power2',
              onComplete: () => text.destroy(),
            });
          },
        });
      },
    });
  }

  // バージョン7: 弾むような動き
  private playAchievementVersion7(text: Phaser.GameObjects.Text): void {
    const startY = text.y;
    text.setScale(0.2);
    text.setAlpha(0);
    text.y = startY + 200;
    
    this.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 1.3,
      scaleY: 1.3,
      y: startY - 80,
      duration: 500,
      ease: 'Bounce.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          y: startY - 20,
          scaleX: 1.1,
          scaleY: 1.1,
          duration: 150,
          yoyo: true,
          repeat: 2,
          ease: 'Power1',
          onComplete: () => {
            this.tweens.add({
              targets: text,
              alpha: 0,
              scaleX: 1.5,
              scaleY: 1.5,
              y: startY - 150,
              duration: 600,
              delay: 500,
              ease: 'Power2',
              onComplete: () => text.destroy(),
            });
          },
        });
      },
    });
  }

  // バージョン8: スケールパルス
  private playAchievementVersion8(text: Phaser.GameObjects.Text): void {
    text.setScale(0.4);
    text.setAlpha(0);
    
    this.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 1.6,
      scaleY: 1.6,
      duration: 400,
      ease: 'Elastic.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          scaleX: 1.0,
          scaleY: 1.0,
          duration: 100,
          onComplete: () => {
            this.tweens.add({
              targets: text,
              scaleX: 1.4,
              scaleY: 1.4,
              duration: 150,
              yoyo: true,
              repeat: 4,
              ease: 'Power1',
              onComplete: () => {
                this.tweens.add({
                  targets: text,
                  alpha: 0,
                  scaleX: 1.8,
                  scaleY: 1.8,
                  duration: 500,
                  delay: 400,
                  ease: 'Power2',
                  onComplete: () => text.destroy(),
                });
              },
            });
          },
        });
      },
    });
  }

  // バージョン9: 3D回転風
  private playAchievementVersion9(text: Phaser.GameObjects.Text): void {
    text.setScale(0.1, 1.5);
    text.setAlpha(0);
    text.setRotation(0);
    
    this.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 1.5,
      scaleY: 1.5,
      rotation: Math.PI * 0.5,
      duration: 500,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          scaleX: 1.2,
          scaleY: 1.2,
          rotation: 0,
          duration: 300,
          ease: 'Power1',
          onComplete: () => {
            this.tweens.add({
              targets: text,
              scaleX: 1.4,
              scaleY: 0.7,
              rotation: Math.PI * 0.25,
              duration: 200,
              yoyo: true,
              repeat: 1,
              ease: 'Power1',
              onComplete: () => {
                this.tweens.add({
                  targets: text,
                  alpha: 0,
                  scaleX: 1.6,
                  scaleY: 1.6,
                  rotation: Math.PI * 0.5,
                  duration: 600,
                  delay: 500,
                  ease: 'Power2',
                  onComplete: () => text.destroy(),
                });
              },
            });
          },
        });
      },
    });
  }

  // バージョン10: 爆発的な拡大
  private playAchievementVersion10(text: Phaser.GameObjects.Text): void {
    text.setScale(0.05);
    text.setAlpha(0);
    
    this.tweens.add({
      targets: text,
      alpha: 1,
      scaleX: 2.0,
      scaleY: 2.0,
      duration: 300,
      ease: 'Power4',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          scaleX: 1.1,
          scaleY: 1.1,
          duration: 100,
          onComplete: () => {
            this.tweens.add({
              targets: text,
              scaleX: 1.5,
              scaleY: 1.5,
              duration: 200,
              yoyo: true,
              repeat: 3,
              ease: 'Power1',
              onComplete: () => {
                this.tweens.add({
                  targets: text,
                  alpha: 0,
                  scaleX: 2.5,
                  scaleY: 2.5,
                  duration: 400,
                  delay: 300,
                  ease: 'Power3',
                  onComplete: () => text.destroy(),
                });
              },
            });
          },
        });
      },
    });
    
    // 色の変化を追加
    const colors = [0xFF0000, 0xFFA500, 0xFFFF00, 0xFFD700];
    let colorIndex = 0;
    const colorTween = this.tweens.addCounter({
      from: 0,
      to: colors.length - 1,
      duration: 1500,
      onUpdate: (tween) => {
        const value = tween.getValue();
        if (value !== null) {
          const index = Math.floor(value);
          if (index !== colorIndex && index < colors.length) {
            colorIndex = index;
            text.setColor(`#${colors[index].toString(16).padStart(6, '0')}`);
          }
        }
      },
    });
    this.time.delayedCall(1500, () => colorTween.stop());
  }

  /**
   * 「Meow」表示エフェクト（控えめ）
   */
  private createNyaaEffect(x: number, y: number): void {
    const nyaaText = this.add.text(x, y - 40, 'Meow', {
      fontSize: '32px',
      fontFamily: 'Tiny5',
      color: '#000000', // 黒色
      fontStyle: 'normal',
      stroke: '#666666',
      strokeThickness: 2,
    });
    nyaaText.setOrigin(0.5);
    nyaaText.setDepth(100);
    nyaaText.setAlpha(0.9); // 少し透明にして控えめに
    
    // 「ニャー」のアニメーション（控えめに上に移動しながらフェードアウト）
    this.tweens.add({
      targets: nyaaText,
      y: y - 100,
      alpha: 0,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 600,
      ease: 'Power1',
      onComplete: () => {
        nyaaText.destroy();
      },
    });
  }

  /**
   * スコア評価テキストを表示（テスト用: 1点ごと）
   */
  private createScoreEvaluationText(x: number, y: number, score: number): void {
    // 既存の評価テキストがあれば削除（重複防止）
    if (this.currentEvaluationText && this.currentEvaluationText.active) {
      this.tweens.killTweensOf(this.currentEvaluationText);
      this.currentEvaluationText.destroy();
      this.currentEvaluationText = undefined;
    }
    
    // スコアに応じた評価テキストを取得（10点ごと）
    let evaluationText: string;
    const scoreTens = Math.floor(score / 10); // 10点単位で計算
    if (scoreTens >= 8) {
      evaluationText = 'Perfect!';
    } else if (scoreTens >= 7) {
      evaluationText = 'Wonderful!';
    } else if (scoreTens >= 6) {
      evaluationText = 'Excellent!';
    } else if (scoreTens >= 5) {
      evaluationText = 'Fantastic!';
    } else if (scoreTens >= 4) {
      evaluationText = 'Amazing!';
    } else if (scoreTens >= 3) {
      evaluationText = 'Great!';
    } else if (scoreTens >= 2) {
      evaluationText = 'Good!';
    } else {
      evaluationText = 'Nice!';
    }

    // 評価テキストをLottieアニメーションの中央に合わせる
    const evalText = this.add.text(x, y, evaluationText, {
      fontSize: '40px',
      fontFamily: 'Tiny5',
      color: '#FFD700', // 金色
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    evalText.setOrigin(0.5);
    evalText.setDepth(100);
    
    // 現在の評価テキストとして保存
    this.currentEvaluationText = evalText;
    
    // 評価テキストのアニメーション（ポップアップ: 拡大してフェードアウト、スライドなし）
    // 最初に小さくしてから拡大（ポップアップ効果）
    evalText.setScale(0.3);
    this.tweens.add({
      targets: evalText,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        // 拡大後、少し待ってからフェードアウト（速度を速く）
        this.tweens.add({
          targets: evalText,
          alpha: 0,
          scaleX: 1.5,
          scaleY: 1.5,
          duration: 800, // 1200msから800msに短縮
          delay: 300, // 500msから300msに短縮
          ease: 'Power2',
          onComplete: () => {
            if (this.currentEvaluationText === evalText) {
              this.currentEvaluationText = undefined;
            }
            evalText.destroy();
          },
        });
      },
    });
  }

  // 床接触によるGame Overは無効化（Time UpのみでGame Over）
  // private onFloorHit(): void {
  //   if (this.isGameOver) return;
  //   this.gameOver();
  // }

  private gameOver(): void {
    if (this.isGameOver) return;
    
    // おじゃまキャラをクリア
    this.clearObstacles();
    
    this.isGameOver = true;
    
    // BGMと音声は既にonObstacleHit()または落下判定で処理されているため、ここでは処理しない
    
    // タイマーを停止
    if (this.updateTimerEvent) {
      this.updateTimerEvent.remove();
      this.updateTimerEvent = undefined;
    }
    
    // 少し遅延してからリザルト画面に遷移（ゲームオーバーの原因を確認できるように）
    this.time.delayedCall(1000, () => {
      // game_endを呼ぶ
      callGameEnd(this.score, () => {
        // コールバックが呼ばれたらリザルト画面へ
        this.scene.start('ResultScene', { score: this.score });
      });
    });
  }

  private pauseGame(): void {
    if (this.isGameOver) return;
    
    this.isPaused = true;
    this.scene.pause();
    this.scene.launch('PauseScene', { 
      playScene: this,
      score: this.score,
      timeRemaining: this.timeRemaining,
    });
  }

  resumeGame(): void {
    this.isPaused = false;
    this.scene.resume();
  }

  /**
   * ミュート状態を切り替え
   */
  private toggleMute(): void {
    this.isMuted = !this.isMuted;
    
    if (this.isMuted) {
      // ミュート: 全てのサウンドを停止
      this.sound.stopAll();
      // サウンドマネージャーをミュート
      this.sound.mute = true;
    } else {
      // アンミュート: サウンドマネージャーのミュートを解除
      this.sound.mute = false;
      // BGMを再開
      const bgm = this.sound.get('bgm');
      if (!bgm || !bgm.isPlaying) {
        const newBgm = this.sound.add('bgm', { loop: true, volume: 0.5 });
        newBgm.play();
      }
    }
    
    // アイコンを更新
    if (this.muteButton) {
      this.muteButton.setTexture(this.isMuted ? 'sounds_off' : 'sounds_on');
    }
  }

  shutdown(): void {
    // シーンが停止する際にタイマーをクリア
    if (this.updateTimerEvent) {
      this.updateTimerEvent.remove();
      this.updateTimerEvent = undefined;
    }
    
    // おじゃまキャラの衝突判定をクリア
    this.obstacleOverlaps.forEach(overlap => {
      if (overlap) {
        overlap.destroy();
      }
    });
    this.obstacleOverlaps = [];
    
    // リングの衝突判定をクリア
    if (this.ringOverlap) {
      this.ringOverlap.destroy();
      this.ringOverlap = undefined;
    }
    
    // super.shutdown(); // Sceneにshutdownメソッドは存在しないためコメントアウト
  }
}

